import type {ReactElement} from 'react';
import {useContext} from 'react';
import {Button, Size, Type} from '@Components/button/button';
import type {ControlledListItemType} from '@Components/controlled-list/controlled-list.types';
import {CONTROLLED_LIST_ITEM_TYPE} from '@Components/controlled-list/controlled-list.types';
import {emailMakerContext} from '@EmailMakerReact/email-maker-react.context';
import {getCurrentIdEmailTemplate} from '@EmailMakerReact/lib/email-maker-storage-library';
import {useIsVersionHistoryAccessible} from '@EmailMakerReact/hooks/use-is-version-history-accessible';
import {NavDropdownMenu} from '../nav-dropdown-menu/nav-dropdown-menu';
import {FILE_MENU_HELP_CENTER_URL, FILE_MENU_NEW_URL, FILE_MENU_SETTINGS_URL, FileMenuOption} from './file-menu.types';
import styles from './file-menu.module.scss';

export function FileMenu(): ReactElement {
  const {editor, actions} = useContext(emailMakerContext);
  const isVersionHistoryAccessible = useIsVersionHistoryAccessible();

  const onClick = (id?: string): void => {
    if (id === FileMenuOption.NEW) {
      window.location.href = window.PMW.util.site_url(FILE_MENU_NEW_URL);
      return;
    }
    if (id === FileMenuOption.DUPLICATE) {
      handleDuplicate();
      return;
    }
    if (id === FileMenuOption.VERSION_HISTORY) {
      actions.openRightPanelVersionHistory();
      return;
    }
    if (id === FileMenuOption.SETTINGS) {
      window.location.href = window.PMW.util.site_url(FILE_MENU_SETTINGS_URL);
      return;
    }
    if (id === FileMenuOption.HELP_CENTER) {
      window.open(FILE_MENU_HELP_CENTER_URL, '_blank', 'noopener');
    }
  };

  function handleDuplicate(): void {
    const idEmailTemplate = editor ? getCurrentIdEmailTemplate(editor) : null;
    if (idEmailTemplate) {
      window.open(window.PMW.util.site_url(`emailmaker/copy/${idEmailTemplate}`), '_blank', 'noopener');
    } else {
      window.alert(window.i18next.t('pmwjs_save_and_try_again'));
    }
  }

  return <NavDropdownMenu selector={renderSelector()} items={getMenuItems(isVersionHistoryAccessible)} onItemClick={onClick} popupClassName={styles.popup} />;
}

function renderSelector(): ReactElement {
  return <Button type={Type.DARK_GHOST} size={Size.SMALL} text={window.i18next.t('pmwjs_file')} />;
}

function getMenuItems(isVersionHistoryAccessible: boolean): ControlledListItemType[] {
  const items: ControlledListItemType[] = [
    {
      id: FileMenuOption.NEW,
      icon: 'icon-plus',
      text: window.i18next.t('pmwjs_new'),
      type: CONTROLLED_LIST_ITEM_TYPE.DEFAULT_7,
    },
    {
      id: FileMenuOption.DUPLICATE,
      icon: 'icon-copy',
      text: window.i18next.t('pmwjs_duplicate'),
      type: CONTROLLED_LIST_ITEM_TYPE.DEFAULT_7,
    },
  ];

  items.push({
    id: FileMenuOption.VERSION_HISTORY,
    icon: 'icon-history',
    text: window.i18next.t('pmwjs_version_history'),
    type: CONTROLLED_LIST_ITEM_TYPE.DEFAULT_7,
    isDisabled: !isVersionHistoryAccessible,
    tooltip: isVersionHistoryAccessible ? undefined : window.i18next.t('pmwjs_save_and_try_again'),
    hasPulsatingDot: true,
  });

  items.push({
    id: FileMenuOption.SETTINGS,
    icon: 'icon-settings',
    text: window.i18next.t('pmwjs_settings'),
    type: CONTROLLED_LIST_ITEM_TYPE.DEFAULT_7,
  });

  items.push({
    id: FileMenuOption.HELP_CENTER,
    icon: 'icon-help',
    text: window.i18next.t('pmwjs_help_center'),
    type: CONTROLLED_LIST_ITEM_TYPE.DEFAULT_7,
  });

  return items;
}
